import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/stores/user';

/**
 * 微信 H5 端路由配置
 * 页面均懒加载；OAuth 流程页（callback/bind）为公开路由，其余需登录态
 *
 * 认证流程：
 *   未登录访问业务页 → 守卫跳转 /auth/callback → 发起微信 OAuth →
 *   微信回调携 code → 换登录态：已绑定进首页；未绑定进 /bind 完成账号绑定
 */
const routes: RouteRecordRaw[] = [
    {
        path: '/auth/callback',
        name: 'AuthCallback',
        component: () => import('../views/AuthCallback.vue'),
        meta: {
            title: '登录中',
            public: true,
        },
    },
    {
        path: '/bind',
        name: 'Bind',
        component: () => import('../views/Bind.vue'),
        meta: {
            title: '绑定账号',
            public: true,
        },
    },
    {
        path: '/',
        redirect: '/home',
    },
    {
        path: '/home',
        name: 'Home',
        component: () => import('../views/Home.vue'),
        meta: {
            title: '首页',
            // 公开页：未绑定用户也可浏览品牌信息与新闻动态（宣传获客），绑定后展示完整服务
            public: true,
        },
    },
    {
        path: '/student',
        name: 'StudentProfile',
        component: () => import('../views/StudentProfile.vue'),
        meta: {
            title: '学生档案',
        },
    },
    {
        path: '/suggestion',
        name: 'SuggestionList',
        component: () => import('../views/SuggestionList.vue'),
        meta: {
            title: '选校建议',
        },
    },
    {
        path: '/contract',
        name: 'ContractDetail',
        component: () => import('../views/ContractDetail.vue'),
        meta: {
            title: '服务合同',
        },
    },
    {
        path: '/material',
        name: 'MaterialList',
        component: () => import('../views/MaterialList.vue'),
        meta: {
            title: '院校申请',
        },
    },
    {
        path: '/news',
        name: 'NewsList',
        component: () => import('../views/NewsList.vue'),
        meta: {
            title: '新闻动态',
            // 公开页：新闻为宣传内容，未绑定用户也可浏览
            public: true,
        },
    },
    {
        path: '/news/:id',
        name: 'NewsDetail',
        component: () => import('../views/NewsDetail.vue'),
        meta: {
            title: '新闻详情',
            public: true,
        },
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

/**
 * 全局路由守卫
 * 1. 设置页面标题
 * 2. 登录鉴权：未登录访问业务页 → 跳 /auth/callback 发起 OAuth（保留回跳地址）
 */
router.beforeEach((to, _from, next) => {
    const title = to.meta['title'] as string | undefined;
    if (title) {
        document.title = `${title} - 卓越睿嘉留学服务`;
    }

    const userStore = useUserStore();
    const isPublic = to.meta['public'] === true;

    if (isPublic || userStore.isLoggedIn) {
        next();
        return;
    }

    // 未登录：进入授权流程页（回调页负责发起 OAuth 并在成功后回跳目标页）
    const redirect = to.path === '/home' ? '' : `&redirect=${encodeURIComponent(to.fullPath)}`;
    next({ path: '/auth/callback', query: { relogin: '1', ...parseQuery(redirect) } });
});

/** 将 "k=v&k2=v2" 串解析为对象（守卫内构造 query 用） */
function parseQuery(queryString: string): Record<string, string> {
    const result: Record<string, string> = {};
    if (!queryString) return result;
    for (const pair of queryString.split('&')) {
        if (!pair) continue;
        const [key, value] = pair.split('=');
        if (key && value !== undefined) {
            result[key] = decodeURIComponent(value);
        }
    }
    return result;
}

export default router;
